import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Input,
  Select,
} from "@fluentui/react-components";
import React from "react";
import { contractTypeEnum } from "../../utils/enums";
import {
  clearanceStatusText,
  delayedAcceptanceStatusText,
  CooperationEndSettlementState,
} from "../../utils/cooperationEnd";
import AppButton from "./AppButton";

interface IUserContractTypeDialogProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  contractType: number | null;
  onContractTypeChange: (value: number | null) => void;
  serviceEndDate: string | null;
  onServiceEndDateChange: (value: string | null) => void;
  hasDelayedServiceAcceptance: boolean;
  onHasDelayedServiceAcceptanceChange: (value: boolean) => void;
  clearanceSettlementState: CooperationEndSettlementState;
  delayedAcceptanceSettlementState: CooperationEndSettlementState;
  clearanceSettledAt: string | null;
  clearanceSettledByName: string | null;
  delayedAcceptanceSettledAt: string | null;
  delayedAcceptanceSettledByName: string | null;
  onSave: () => void;
  isLoading?: boolean;
}

const fieldLabelStyle: React.CSSProperties = {
  marginBottom: "8px",
  marginTop: "16px",
  fontWeight: 600,
};

const hintStyle: React.CSSProperties = {
  marginTop: "4px",
  color: "#605e5c",
  fontSize: "12px",
};

const statusStyle: React.CSSProperties = {
  marginTop: "16px",
  fontSize: "13px",
  lineHeight: 1.4,
};

function calendarDay(value: string | null): string {
  return value ? value.slice(0, 10) : "";
}

const UserContractTypeDialog: React.FC<IUserContractTypeDialogProps> = ({
  open,
  setOpen,
  contractType,
  onContractTypeChange,
  serviceEndDate,
  onServiceEndDateChange,
  hasDelayedServiceAcceptance,
  onHasDelayedServiceAcceptanceChange,
  clearanceSettlementState,
  delayedAcceptanceSettlementState,
  clearanceSettledAt,
  clearanceSettledByName,
  delayedAcceptanceSettledAt,
  delayedAcceptanceSettledByName,
  onSave,
  isLoading,
}) => {
  return (
    <Dialog open={open} onOpenChange={(_, data) => setOpen(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Zmień typ umowy</DialogTitle>
          <DialogContent>
            <div style={{ marginBottom: "8px", fontWeight: 600 }}>Typ umowy</div>
            <Select
              value={contractType === null ? "" : String(contractType)}
              onChange={(_, selectData) => {
                const value = selectData.value;
                onContractTypeChange(value === "" ? null : Number(value));
              }}
            >
              <option value="">Wybierz typ umowy</option>
              <option value={contractTypeEnum.B2B}>Umowa B2B</option>
              <option value={contractTypeEnum.EmploymentContract}>Umowa o pracę</option>
              <option value={contractTypeEnum.ContractOfMandate}>Umowa zlecenie</option>
              <option value={contractTypeEnum.Intern}>Praktykant/Stażysta</option>
            </Select>

            <div style={fieldLabelStyle}>Termin zakończenia usług</div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <Input
                type="date"
                value={calendarDay(serviceEndDate)}
                onChange={(_, data) => onServiceEndDateChange(data.value || null)}
              />
              <Button
                appearance="secondary"
                disabled={!serviceEndDate || isLoading}
                onClick={() => onServiceEndDateChange(null)}
              >
                Wyczyść
              </Button>
            </div>
            <div style={hintStyle}>Pole może pozostać puste.</div>

            <div style={{ marginTop: "16px" }}>
              <Checkbox
                checked={hasDelayedServiceAcceptance}
                onChange={(_, data) => onHasDelayedServiceAcceptanceChange(Boolean(data.checked))}
                label="Pracuje w projektach z odroczonym odbiorem usług i płatnością"
              />
              <div style={hintStyle}>
                Protokół dla zamawiającego i płatność są później niż miesiąc świadczenia usług.
              </div>
            </div>

            <div style={statusStyle}>
              <div>
                {clearanceStatusText(
                  clearanceSettlementState,
                  clearanceSettledAt,
                  clearanceSettledByName
                )}
              </div>
              <div>
                {delayedAcceptanceStatusText(
                  delayedAcceptanceSettlementState,
                  delayedAcceptanceSettledAt,
                  delayedAcceptanceSettledByName
                )}
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Anuluj</Button>
            </DialogTrigger>
            <AppButton
              text="Zapisz"
              disabled={isLoading}
              appearance="primary"
              onClick={onSave}
            />
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default UserContractTypeDialog;
